import easingFunctions from '../../../app/helpers/easing';
// import { getYForX } from './core/getYForX';
import React, { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

const scalePoints = (points, width, height) => {
  return points.map((point) => ({
    x: point.x * width,
    y: (1 - point.y) * height,
  }));
};

const unScalePoint = (point, width, height) => ({
  x: point.x / width,
  y: point.y / height,
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function BezierSplineEditor({ width: propsWidth, easingFn = 'linear', isBezierMain = true, indicatorSpeed = 5, points: propsPoints, onPointsChange, confirm }) {
  const [sizes, setSizes] = useState([propsWidth || '100%', 0]);
  const [ready, setReady] = useState(false);

  const width = sizes[0];
  const height = sizes[1];

  const controlPointRadius = 8;
  const padding = 0;

  const containerRef = useRef(null);
  const svgRef = useRef(null);
  // const circleRef = useRef(null);
  const confirmRef = useRef(null);

  const easingPath = useMemo(() => {
    if (!ready) {
      return '';
    }

    let pathData = '';
    const delta = 0.01;

    const fn = easingFunctions[easingFn];

    for (let t = 0; t <= 1 + delta; t += delta) {
      pathData += ` ${t === 0 ? 'M' : 'L'} ${t * width} ${(1 - fn(t)) * height} `;
    }

    return pathData;
  }, [ready, easingFn, width, height]);

  const relativePoints = useMemo(() => {
    return [
      { x: 0, y: 0 },
      { x: propsPoints[0], y: propsPoints[1] },
      { x: propsPoints[2], y: propsPoints[3] },
      { x: 1, y: 1 },
    ];
  }, [propsPoints]);

  const scaledPoints = useMemo(() => scalePoints(relativePoints, width, height), [width, height, relativePoints]);

  const pathData = `M ${scaledPoints[0].x} ${scaledPoints[0].y} C ${scaledPoints
    .slice(1)
    .map((p) => `${p.x} ${p.y}`)
    .join(', ')}`;

  const handleDrag = (event, index) => {
    if (!svgRef.current) return;

    event.preventDefault();

    const rect = svgRef.current.getBoundingClientRect();

    const scale = rect.width / width;

    let x = (event.clientX - rect.left - padding) / scale;
    let y = (event.clientY - rect.top - padding) / scale;

    // Constrain all points within the bounding box
    x = Math.max(0, Math.min(rect.width - padding * 2, x));
    y = Math.max(0, Math.min(rect.height - padding * 2, y));

    const newPoints = [...relativePoints];
    if (index === 0 || index === relativePoints.length - 1) {
      newPoints[index] = { x: scaledPoints[index].x, y: y };
    } else {
      newPoints[index] = { x: x, y: y };
    }

    newPoints[index] = unScalePoint(newPoints[index], width, height);

    // flip the y axis
    newPoints[index].y = 1 - newPoints[index].y;

    onPointsChange([newPoints[1].x, newPoints[1].y, newPoints[2].x, newPoints[2].y]);
  };

  useLayoutEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    const size = Math.floor(rect.width);

    setSizes([size, size]);
    setReady(true);
  }, []);

  useEffect(() => {
    confirmRef.current = confirm;
  }, [confirm]);

  /////////////////////

  // useEffect(() => {
  //   const updateCirclePosition = () => {
  //     if (!circleRef.current) return;
  //
  //     const t = (performance.now() / indicatorSpeed) % width; // Time in seconds
  //
  //     const x = t; //-radius + 2 * radius * t;
  //     const y = (1 - getYForX(relativePoints, x / width)) * height;
  //
  //     circleRef.current.setAttribute('cx', x.toString());
  //     circleRef.current.setAttribute('cy', y.toString());
  //
  //     requestAnimationFrame(updateCirclePosition);
  //   };
  //   updateCirclePosition();
  // }, [relativePoints, indicatorSpeed, ready]);

  return (
    <div
      ref={containerRef}
      style={{
        width,
        height,
        paddingTop: height === 0 ? '100%' : 0,
        border: '1px solid #454545',
        borderRadius: 5,
      }}
    >
      {ready && (
        <svg
          ref={svgRef}
          width={width}
          height={height}
          style={{
            overflow: 'visible',
            padding: `${padding}px`,
          }}
        >
          <line x1={0} y1={height * 0.5} x2={width} y2={height * 0.5} stroke="#333333" />
          <line x1={width * 0.5} y1={0} x2={width * 0.5} y2={height} stroke="#333333" />

          <path d={pathData} stroke={isBezierMain ? '#4285f4' : '#454545'} fill="none" />

          <path d={easingPath} stroke={isBezierMain ? '#454545' : '#4285f4'} fill="none" />

          {scaledPoints.map((point, index) => {
            if (index % 3 === 1) {
              return (
                <Fragment key={index}>
                  <line
                    key={`${index}-1`}
                    x1={scaledPoints[index - 1].x}
                    y1={scaledPoints[index - 1].y}
                    x2={point.x}
                    y2={point.y}
                    stroke={isBezierMain ? '#4285f4' : '#454545'}
                    strokeDasharray="5,5"
                  />
                  <line
                    key={`${index}-2`}
                    x1={scaledPoints[index + 1].x}
                    y1={scaledPoints[index + 1].y}
                    x2={scaledPoints[index + 2].x}
                    y2={scaledPoints[index + 2].y}
                    stroke={isBezierMain ? '#4285f4' : '#454545'}
                    strokeDasharray="5,5"
                  />
                </Fragment>
              );
            }
            return null;
          })}

          {scaledPoints.map((point, index) => {
            if (index === 0 || index === scaledPoints.length - 1) {
              return <circle key={index} cx={point.x} cy={point.y} r={5} fill="#454545" />;
            } else {
              return (
                <circle
                  key={index}
                  cx={point.x}
                  cy={point.y}
                  r={controlPointRadius}
                  style={{
                    cursor: 'move',
                  }}
                  fill={isBezierMain ? '#4285f4' : '#454545'}
                  onTouchStart={(e) => e.preventDefault()}
                  onPointerDown={(e) => {
                    e.preventDefault();

                    const listenerMove = (event) => {
                      event.preventDefault();
                      event.stopPropagation();

                      handleDrag(event, index);
                    };

                    const listenerUp = (event) => {
                      event.preventDefault();

                      document.body.removeEventListener('pointermove', listenerMove);
                      document.body.removeEventListener('pointerup', listenerUp);

                      confirmRef.current();
                    };

                    document.body.addEventListener('pointermove', listenerMove);
                    document.body.addEventListener('pointerup', listenerUp);
                  }}
                />
              );
            }
          })}
        </svg>
      )}
    </div>
  );
}

export default BezierSplineEditor;

// <circle ref={circleRef} cx="10" cy="10" r="5" fill="darkgray" />
